import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { obterMunicipiosPorUF, obterUFs } from '../../api/recursosAPI';
import { RootState } from '../../app/store';
import authIdPositiva from '../../auth/AuthIdPositiva';
import * as mensagens from '../../common/Mensagens';
import { MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA, MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA_WEB } from '../../common/Mensagens';
import { IdentificacaoPositivaModel } from '../../model/IdentificacaoPositiva';
export interface IdentificacaoPositivaState {
  ufs: any[];
  municipios: any[];
  nomeCompleto: string;
  nomeDaMae: string;
  dataNascimento: any;
  numeroFamiliar: number;
  ufSelecionada: number;
  municipioSelecionado: number;
  logado: boolean;
  mensagem: string;
  tipoMensagem: string;
  ativarMensagem: boolean;
  passCode: string;
  temComunicacaoComAPI: boolean;
  versaoMinimaAceitavel: boolean;
}

const initialState: IdentificacaoPositivaState = {
  ufs: [],
  municipios: [],
  nomeCompleto: '',
  dataNascimento: '',
  nomeDaMae: '',
  ufSelecionada: 0,
  municipioSelecionado: 0,
  logado: authIdPositiva.isAuthenticated(),
  mensagem: '',
  tipoMensagem: '',
  ativarMensagem: false,
  passCode: '',
  numeroFamiliar: 0,
  temComunicacaoComAPI: false,
  versaoMinimaAceitavel: true
};

export const getUfs = createAsyncThunk('identificacaoPositiva/getUfs', async () => {
  return obterUFs();
});

export const getMunicipios = createAsyncThunk(
  'identificacaoPositiva/getMunicipios',
  async (codigoUfIbge: number) => {
    return obterMunicipiosPorUF(codigoUfIbge);
  }
);

export const login = createAsyncThunk(
  'identificacaoPositiva/login',
  async (identificacaoPositiva: IdentificacaoPositivaModel): Promise<any> => {
    return authIdPositiva.login(identificacaoPositiva);
  }
);

export const identificacaoPositivaSlice = createSlice({
  name: 'identificacaoPositiva',
  initialState,
  reducers: {
    selecionarUF: (state, action) => {
      state.ufSelecionada = action.payload;
    },
    selecionarMunicipio: (state, action) => {
      state.municipioSelecionado = action.payload;
    },
    alterarNomeCompleto: (state, action) => {
      state.nomeCompleto = action.payload;
    },
    alterarDataNascimento: (state, action) => {
      state.dataNascimento = action.payload;
    },
    alterarNomeDaMae: (state, action) => {
      state.nomeDaMae = action.payload;
    },
    ativarMensagem: (state, action) => {
      state.ativarMensagem = action.payload;
    },
    alterarMensagem: (state, action) => {
      state.mensagem = action.payload;
    },
    alterarTipoMensagem: (state, action) => {
      state.tipoMensagem = action.payload;
    },
    atualizarPassCode: (state, action) => {
      state.passCode = action.payload;
    },
    resetPassCode: (state, action) => {
      state.passCode = '';
    },
    resetMunicipios: (state) => {
      state.municipios = initialState.municipios;
    },
    limparFormulario: (state) => {
      state.nomeCompleto = initialState.nomeCompleto;
      state.dataNascimento = initialState.dataNascimento;
      state.nomeDaMae = initialState.nomeDaMae;
      state.ufSelecionada = initialState.ufSelecionada;
      state.municipioSelecionado = initialState.municipioSelecionado;
    },
    resetIdentificacaoPositiva: (state) => (state = initialState),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUfs.fulfilled, (state, action) => {
        state.ufs = action.payload;
        state.temComunicacaoComAPI = true;
      })
      .addCase(getUfs.rejected, (state, action) => {
        if (action.error?.message && (action.error?.message.includes(MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA) ||
          action.error?.message.includes(MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA_WEB))) {
          state.versaoMinimaAceitavel = false;
        }else{
          state.temComunicacaoComAPI = false;
        }
      })
      .addCase(getMunicipios.fulfilled, (state, action) => {
        state.municipios = action.payload;
        state.temComunicacaoComAPI = true;
      })
      .addCase(getMunicipios.rejected, (state, action) => {
        state.temComunicacaoComAPI = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        const dadosCadastro = action.payload.data.pessoa.pessoaDadosCadastroDTO;
        state.nomeDaMae = dadosCadastro.nomeCompletoMae;
        state.nomeCompleto = dadosCadastro.nomePessoa;
        state.numeroFamiliar = action.payload.data.pessoa.numeroFamiliar;
        state.logado = action.payload.autenticado;
      })
      .addCase(login.rejected, (state, error) => {
        state.logado = false;
        state.tipoMensagem = 'warning';
        state.mensagem = mensagens.MSG_CAD_UNICO_NAO_CADASTRADO;
        state.ativarMensagem = true;
      });
  },
});

export const {
  selecionarUF,
  selecionarMunicipio,
  alterarNomeCompleto,
  alterarDataNascimento,
  alterarNomeDaMae,
  ativarMensagem,
  alterarMensagem,
  alterarTipoMensagem,
  atualizarPassCode,
  resetPassCode,
  resetMunicipios,
  limparFormulario,
  resetIdentificacaoPositiva
} = identificacaoPositivaSlice.actions;

export const selectVersaoMinimaAceitavel = (state: RootState) => state.identificacaoPositiva.versaoMinimaAceitavel;
export const selectComunicacaoAPI = (state: RootState) => state.identificacaoPositiva.temComunicacaoComAPI;
export const selectUfs = (state: RootState) => state.identificacaoPositiva.ufs;
export const selectMunicipios = (state: RootState) => state.identificacaoPositiva.municipios;
export const selectPassCode = (state: RootState) => state.identificacaoPositiva.passCode;
export const selectLogado = (state: RootState) => state.identificacaoPositiva.logado;
export const selectState = (state: RootState) => state.identificacaoPositiva;

export default identificacaoPositivaSlice.reducer;
